import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { ImagePreview } from "../components/ImagePreview";
import { Spinner } from "../components/Spinner";
import { Tooltip } from "../components/Tooltip";
import { XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React, { useMemo } from "react";
const CitationContext = React.createContext({
    compact: false,
});
const citationVariants = cva("relative flex min-w-24 flex-none overflow-hidden", {
    variants: {
        hasImage: {
            // Use min() to maintain aspect ratio in grid mode (8% of width) while capping
            // padding at 3 (0.75rem) for list mode to prevent excessive top padding on wide items.
            false: "pt-[min(8%,theme(spacing.3))]",
            true: "border-0 p-0",
        },
        compact: {
            true: "flex-row flex-wrap items-center gap-x-2",
            false: "flex-col",
        },
    },
    defaultVariants: {
        hasImage: false,
        compact: false,
    },
});
/**
 * A clickable source reference shown alongside agent answers: a composable surface
 * filled with CitationIcons (plus an optional CitationIndex), CitationTitle, and
 * CitationDescription, with CitationImage for image sources and a CitationClose
 * action to make it dismissable. Use it to attribute an answer to a document, web
 * page, Slack thread, or table, laid out with CitationGrid; for plain file
 * attachments, use AttachmentChip instead.
 * @summary Source reference card.
 */
const Citation = React.forwardRef(({ children, compact = false, variant = "secondary", isLoading, loadingLabel, className, tooltip, ...props }, ref) => {
    const { hasDescription, hasImage } = React.useMemo(() => {
        const childrenArray = React.Children.toArray(children);
        return {
            hasDescription: childrenArray.some((child) => React.isValidElement(child) && child.type === CitationDescription),
            hasImage: childrenArray.some((child) => React.isValidElement(child) && child.type === CitationImage),
        };
    }, [children]);
    // IMPORTANT: The order of elements is crucial for event handling.
    // The CitationDescription must always come after other elements to ensure
    // proper event propagation (especially for the close button's click events).
    // If auto-inserting a description, it must be appended after children.
    // Skip auto-insertion for CitationImage children since they're self-contained.
    const contentWithDescription = (React.createElement(React.Fragment, null,
        children,
        !hasDescription && !hasImage && !compact && (React.createElement(CitationDescription, null, "\u00A0"))));
    const citationContextValue = useMemo(() => ({ compact }), [compact]);
    const cardButton = (React.createElement(Card, { ref: ref, variant: variant, size: "sm", className: cn(citationVariants({ hasImage, compact }), className), ...props },
        React.createElement(CitationContext.Provider, { value: citationContextValue }, contentWithDescription),
        isLoading && React.createElement(CitationLoading, { label: loadingLabel })));
    if (tooltip) {
        return React.createElement(Tooltip, { trigger: cardButton, label: tooltip });
    }
    return cardButton;
});
Citation.displayName = "Citation";
/** Small numbered badge for a citation, used for numbered inline references. */
const CitationIndex = React.forwardRef(({ children, className, ...props }, ref) => {
    return (React.createElement("div", { ref: ref, className: cn("z-10", "heading-xs flex h-4 w-4 items-center justify-center rounded-full", "text-primary-200", "bg-primary-600", className), ...props }, children));
});
CitationIndex.displayName = "CitationIndex";
const CITATION_GRID_VARIANTS = ["grid", "list"];
const citationGridVariants = cva("grid gap-2", {
    variants: {
        variant: {
            grid: "grid-cols-2 @xxs:grid-cols-3 @xs:grid-cols-4 @md:grid-cols-5 @lg:grid-cols-6",
            list: "grid-cols-1",
        },
        reversed: {
            true: "rotate-180 [&>*]:rotate-180",
            false: "",
        },
    },
    defaultVariants: {
        variant: "grid",
        reversed: false,
    },
});
/** Responsive container for laying out multiple Citations as a grid or list. */
const CitationGrid = React.forwardRef(({ children, className, variant, reversed, ...props }, ref) => {
    return (React.createElement("div", { ref: ref, className: cn("min-w-60 @container", className), ...props },
        React.createElement("div", { className: citationGridVariants({ variant, reversed }) }, children)));
});
CitationGrid.displayName = "CitationGrid";
/** Close button for a dismissable Citation, typically passed as its `action`. */
const CitationClose = React.forwardRef(({ className, onClick, ...props }, ref) => {
    return (React.createElement(Button, { ref: ref, variant: "ghost", size: "icon", className: className, icon: XClose, onClick: (e) => {
            e.stopPropagation();
            onClick === null || onClick === void 0 ? void 0 : onClick(e);
        }, ...props }));
});
CitationClose.displayName = "CitationClose";
/** Image preview filling a Citation, for image sources. */
const CitationImage = React.forwardRef(({ imgSrc, alt, title, downloadUrl, isLoading, onClose, onClick, className }, ref) => {
    return (React.createElement(ImagePreview, { ref: ref, imgSrc: imgSrc, alt: alt, title: title, downloadUrl: downloadUrl, isLoading: isLoading, onClose: onClose ? () => onClose() : undefined, onClick: onClick, className: className, variant: "embedded", titlePosition: "bottom" }));
});
CitationImage.displayName = "CitationImage";
/** Icon row of a Citation: source logo/favicon and optional CitationIndex. */
const CitationIcons = React.forwardRef(({ children, className, ...props }, ref) => {
    const { compact } = React.useContext(CitationContext);
    return (React.createElement("div", { ref: ref, className: cn("flex items-center gap-2", !compact && "pb-1", className), ...props }, children));
});
CitationIcons.displayName = "CitationIcons";
const CitationLoading = React.forwardRef(({ className, label, ...props }, ref) => {
    return (React.createElement("div", { ref: ref, className: cn("absolute inset-0 z-20 flex h-full w-full flex-col items-center justify-center gap-1 rounded-xl backdrop-blur-sm", "bg-primary-100/80", className), ...props },
        React.createElement(Spinner, { size: "md" }),
        label != null && (React.createElement("span", { className: "heading-xs font-mono text-muted-foreground" }, label))));
});
CitationLoading.displayName = "CitationLoading";
/** Single-line, truncating title of a Citation. */
const CitationTitle = React.forwardRef(({ children, className, ...props }, ref) => {
    const { compact } = React.useContext(CitationContext);
    return (React.createElement("div", { ref: ref, className: cn("z-10", "line-clamp-1 overflow-hidden text-ellipsis break-all", "heading-sm", "text-foreground", compact && "flex-1 min-w-0", className), ...props }, children));
});
CitationTitle.displayName = "CitationTitle";
/** Single-line, truncating muted description of a Citation. */
const CitationDescription = React.forwardRef(({ children, className, ...props }, ref) => {
    const { compact } = React.useContext(CitationContext);
    return (React.createElement("div", { ref: ref, className: cn("z-10", "line-clamp-1 overflow-hidden text-ellipsis", "text-xs font-normal", "text-muted-foreground", compact && "basis-full", className), ...props }, children));
});
CitationDescription.displayName = "CitationDescription";
export { Citation, CitationClose, CitationDescription, CitationGrid, CitationIcons, CitationImage, CitationIndex, CitationTitle, };
//# sourceMappingURL=Citation.js.map